import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Breadcrumbs, Button, Card,
  CardContent, Chip, CircularProgress, FormControlLabel, Grid, LinearProgress, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { ContentCopy, Dns, ExpandMore, Refresh } from '@mui/icons-material';
import { GlobalAdminOnly } from '../components/PermissionGate';
import { PageTransition } from '../components/PageTransition';
import api from '../services/api';
import { WarmFleetPool } from '../types/providers';
import { detailSurfaceSx } from '../utils/surfaces';
import { useNotification } from '../contexts/NotificationContext';

interface FleetHost {
  id: string;
  pool_id?: string | null;
  instance_id: string | null;
  public_ip: string | null;
  state: string;
  capacity: number;
  provider_name: string;
  tenant_name: string;
  region: string;
  error: string | null;
  created_at: string;
  updated_at: string;
  bots: { id: string; slot: number; status: string; public_ip: string | null; scenario_id: string }[];
}

const FleetContent: React.FC = () => {
  const [hosts, setHosts] = useState<FleetHost[]>([]);
  const [pools, setPools] = useState<WarmFleetPool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [includeTerminated, setIncludeTerminated] = useState(false);
  const [search, setSearch] = useState('');
  const [updated, setUpdated] = useState<Date | null>(null);
  const generation = useRef(0);
  const { showNotification } = useNotification();
  const refresh = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    try {
      const response = await api.get(`/container-fleet?include_terminated=${includeTerminated}`);
      if (request !== generation.current) return;
      setHosts(response.data.hosts);
      setPools(response.data.pools || []);
      setUpdated(new Date());
      setError('');
    } catch {
      if (request === generation.current) setError('Could not refresh the fleet. The last successful snapshot is shown.');
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [includeTerminated]);
  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => { if (!document.hidden) void refresh(); }, 15000);
    return () => { window.clearInterval(timer); generation.current += 1; };
  }, [refresh]);
  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      showNotification('SSH command copied', 'success');
    } catch { showNotification('Could not copy. Select the displayed SSH command to copy it manually.', 'error'); }
  };
  const active = hosts.filter(h => h.state !== 'terminated');
  const bots = active.reduce((sum, h) => sum + h.bots.length, 0);
  const capacity = active.reduce((sum, h) => sum + h.capacity, 0);
  const visible = hosts.filter(h => [h.instance_id, h.public_ip, h.provider_name, h.tenant_name, h.region,
    ...h.bots.flatMap(b => [b.id, b.public_ip])].some(value => value?.toLowerCase().includes(search.toLowerCase())));

  return <PageTransition><Box>
    <Breadcrumbs sx={{ mb: 2, '& a': { color: 'primary.main' } }}><Link to="/maintenance">Maintenance</Link><Typography color="text.primary">Fleet Manager</Typography></Breadcrumbs>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
      <Box><Typography variant="h4" component="h1" fontWeight="bold">Fleet Manager</Typography>
        <Typography color="text.secondary">AWS container hosts · parallel startup · scenario warm pools</Typography></Box>
      <Button startIcon={<Refresh />} onClick={() => void refresh()} disabled={loading}>Refresh</Button>
    </Stack>
    {pools.map(pool => <Alert key={pool.id} severity={pool.error ? 'warning' : 'info'} sx={{ mb: 2 }}>
      <Typography fontWeight="bold">{pool.provider_name} · {pool.region}</Typography>
      Minimum {pool.minimum_hosts} hosts · {pool.ready_empty_hosts} ready empty · {pool.starting_hosts} starting · {pool.occupied_hosts} occupied · {pool.total_hosts}/{pool.target_hosts} hosts
      {pool.error && <Typography variant="body2">{pool.error}</Typography>}
    </Alert>)}
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {[['Active hosts', active.length], ['Allocated bots', bots], ['Available slots', capacity - bots]].map(([label, value]) =>
        <Grid item xs={12} sm={4} key={label}><Card variant="outlined"><CardContent>
          <Typography color="text.secondary">{label}</Typography><Typography variant="h3">{value}</Typography>
        </CardContent></Card></Grid>)}
    </Grid>
    <Typography color="text.secondary" sx={{ mb: 2 }}>
      Capacity is configured per instance type. Stopped bots keep their slots. Warm pools are scoped to one tenant and scenario; excess empty hosts are released.
    </Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
      <TextField label="Find host, tenant, bot or IP" value={search} onChange={e => setSearch(e.target.value)} size="small" sx={{ flex: 1 }} />
      <FormControlLabel control={<Switch checked={includeTerminated} onChange={(_, value) => setIncludeTerminated(value)} />} label="Show all terminated hosts (recent failures are always shown)" />
    </Stack>
    {error && <Alert severity="error" sx={{ mb: 2 }} role="alert">{error}</Alert>}
    <Typography variant="caption" color="text.secondary" component="p" aria-live="polite" sx={{ mb: 2 }}>
      {updated ? `Last refreshed ${updated.toLocaleTimeString()} · refreshes every 15 seconds` : 'Loading fleet state…'}
    </Typography>
    {loading && !updated && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress aria-label="Loading fleet" /></Box>}
    {!loading && visible.length === 0 && <Alert severity="info">{search ? 'No hosts match this search.' : 'No hosts to display. Request Docker bots to create the first host.'}</Alert>}
    {visible.map(host => <Accordion key={host.id} disableGutters sx={{ mb: 2, border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMore />} aria-controls={`host-${host.id}`} id={`host-heading-${host.id}`}>
        <Box sx={{ width: '100%', pr: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Dns color="primary" /><Typography fontFamily="monospace" fontWeight="bold">{host.instance_id || 'Awaiting host creation'}</Typography>
            <Chip size="small" variant="outlined" label={host.state === 'ready' && host.pool_id && host.bots.length === 0 ? 'warm · empty' : host.state === 'terminated' && host.error ? 'failed · released' : host.state} color={host.state === 'ready' ? 'success' : host.error ? 'warning' : 'default'} />
            <Typography variant="body2">{host.bots.length} / {host.capacity} bots</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>{host.tenant_name} · {host.provider_name} · {host.region} · {host.public_ip || 'IP pending'}</Typography>
          <LinearProgress variant="determinate" value={100 * host.bots.length / host.capacity} aria-label={`${host.bots.length} of ${host.capacity} slots allocated`} />
        </Box>
      </AccordionSummary>
      <AccordionDetails id={`host-${host.id}`}>
        {host.error && <Alert severity="warning" sx={{ mb: 2 }}>{host.error}</Alert>}
        <Box sx={detailSurfaceSx}>
          <Typography variant="subtitle2">Host access</Typography>
          <Typography variant="body2" color="text.secondary">Management IP: {host.public_ip || 'Pending'} · Use the platform operator SSH key.</Typography>
          {host.public_ip && host.state !== 'terminated' && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Typography component="code" sx={{ overflowWrap: 'anywhere' }}>ssh ubuntu@{host.public_ip}</Typography>
            <Button size="small" startIcon={<ContentCopy />} onClick={() => void copy(`ssh ubuntu@${host.public_ip}`)}>Copy SSH command</Button>
          </Stack>}
          <Typography variant="caption" color="text.secondary">Created {new Date(host.created_at + 'Z').toLocaleString()} · Last state update {new Date(host.updated_at + 'Z').toLocaleString()}</Typography>
        </Box>
        <TableContainer sx={{ mt: 2 }}><Table size="small" aria-label={`Bot distribution on ${host.instance_id || host.id}`}>
          <TableHead><TableRow><TableCell>Slot</TableCell><TableCell>Bot</TableCell><TableCell>Status</TableCell><TableCell>Public IP</TableCell><TableCell>Scenario</TableCell></TableRow></TableHead>
          <TableBody>{[...host.bots].sort((a, b) => a.slot - b.slot).map(bot => <TableRow key={bot.id}>
            <TableCell>{bot.slot + 1}</TableCell><TableCell sx={{ fontFamily: 'monospace' }}>{bot.id}</TableCell><TableCell>{bot.status}</TableCell>
            <TableCell sx={{ fontFamily: 'monospace' }}>{bot.public_ip || 'Pending'}</TableCell><TableCell>{bot.scenario_id}</TableCell>
          </TableRow>)}{host.bots.length === 0 && <TableRow><TableCell colSpan={5}>No allocated bots. Empty hosts are removed automatically.</TableCell></TableRow>}</TableBody>
        </Table></TableContainer>
      </AccordionDetails>
    </Accordion>)}
  </Box></PageTransition>;
};

const FleetManager: React.FC = () => <GlobalAdminOnly fallback={<Alert severity="warning">Fleet Manager requires global administrator access.</Alert>}><FleetContent /></GlobalAdminOnly>;
export default FleetManager;
